import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Building2, Search, ExternalLink, Plus, Wand2, Loader2, ListChecks, Eye, RotateCcw, GitCompare, Users, EyeOff, ArrowRight, Radar } from "lucide-react";
import { fmtDate } from "@/lib/constants";
import { FindDecisionMaker } from "@/components/FindDecisionMaker";
import OpportunityTriggerFinder from "@/pages/OpportunityTriggerFinder";

const EXP_STYLES = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};
const CUI_STYLES = {
  Yes: "bg-red-100 text-red-800", Likely: "bg-amber-100 text-amber-800",
  Possible: "bg-blue-100 text-blue-800", Unknown: "bg-slate-100 text-slate-600",
};
const LEAD_STATUS_STYLES = {
  "New Prospect": "bg-slate-100 text-slate-600 border-slate-200",
  "Already in Leads": "bg-navy/10 text-navy border-navy/20",
  "Possible Duplicate": "bg-gold/20 text-gold-dark border-gold/40",
  "Existing Customer": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Closed Lost / Nurture": "bg-slate-200 text-slate-700 border-slate-300",
};
const LEAD_STATUS_FILTERS = [
  { value: "All", label: "All statuses" },
  { value: "New Prospect", label: "New Prospects Only" },
  { value: "Already in Leads", label: "Already in Leads" },
  { value: "Possible Duplicate", label: "Possible Duplicates" },
  { value: "Existing Customer", label: "Existing Customers" },
  { value: "Closed Lost / Nurture", label: "Closed Lost / Nurture" },
];
const CHECKLIST = ["Website found", "Phone found", "Email found", "CAGE verified",
  "Apollo enrichment run", "Decision-maker identified", "Added to outreach sequence", "Added to HubSpot"];

function usd(n) { return "$" + (n || 0).toLocaleString(); }

export default function ContractorFinder() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    agency: "Department of Defense", subtier: "", awardee_name: "", naics: "541512", psc: "",
    keywords: "", place_of_performance: "", date_from: "", date_to: "",
    dollars_min: 100000, dollars_max: 50000000, status: "Active", min_exposure: 0,
  });
  const [state, setState] = useState({ status: "idle", results: [] });
  const [selected, setSelected] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [checks, setChecks] = useState({});
  const [showDM, setShowDM] = useState(false);
  const [adding, setAdding] = useState(false);
  const [leadStatusFilter, setLeadStatusFilter] = useState("All");
  const [hideExisting, setHideExisting] = useState(false);
  const [reviewMatch, setReviewMatch] = useState(null); // { rec, match }

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const search = async () => {
    setState({ status: "loading", results: [] });
    try {
      const res = await api.post("/contractors/search", filters);
      setState(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Search failed";
      setState({ status: "error", message: msg, results: [] });
      toast.error(msg);
    }
  };

  const openDetail = async (rec) => {
    setSelected(rec); setGuidance(null); setChecks({});
    try { const res = await api.post("/contractors/guidance", { contractor: rec }); setGuidance(res.data); }
    catch { /* non-blocking */ }
  };

  const addToLeads = async (rec, contact = null, note = null, force = false) => {
    setAdding(true);
    try {
      const res = await api.post("/contractors/add-to-leads", { contractor: rec, contact, note, force });
      toast.success("Contractor added to Leads");
      setSelected(null); setShowDM(false); setReviewMatch(null);
      navigate(`/leads/${res.data.id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        setReviewMatch({ rec, match: detail.match });
        setShowDM(false);
      } else {
        toast.error("Failed to add lead");
      }
    } finally { setAdding(false); }
  };

  const goToLead = (id) => { if (id) navigate(`/leads/${id}`); };

  const visibleResults = (state.results || []).filter((r) => {
    if (hideExisting && r.lead_status && r.lead_status !== "New Prospect") return false;
    if (leadStatusFilter !== "All" && r.lead_status !== leadStatusFilter) return false;
    return true;
  });

  // Renders the correct action control(s) for a result based on its lead status.
  const RowAction = ({ r, i }) => {
    const m = r.lead_match || {};
    const id = m.matchedLeadId;
    switch (r.lead_status) {
      case "Already in Leads":
        return (
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" data-testid={`cf-view-${i}`} onClick={() => goToLead(id)}><Eye className="h-3.5 w-3.5 mr-1" /> View Lead</Button>
            <Button size="sm" variant="ghost" className="text-navy" data-testid={`cf-update-${i}`} onClick={() => goToLead(id)}>Update</Button>
          </div>
        );
      case "Possible Duplicate":
        return <Button size="sm" variant="outline" className="border-gold text-gold-dark hover:bg-gold/10" data-testid={`cf-review-${i}`} onClick={() => setReviewMatch({ rec: r, match: m })}><GitCompare className="h-3.5 w-3.5 mr-1" /> Review Match</Button>;
      case "Existing Customer":
        return <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`cf-view-customer-${i}`} onClick={() => goToLead(id)}><Users className="h-3.5 w-3.5 mr-1" /> View Customer</Button>;
      case "Closed Lost / Nurture":
        return (
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" data-testid={`cf-reopen-${i}`} onClick={() => goToLead(id)}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen Lead</Button>
            <Button size="sm" variant="ghost" data-testid={`cf-history-${i}`} onClick={() => goToLead(id)}>History</Button>
          </div>
        );
      default:
        return <Button size="sm" variant="outline" data-testid={`cf-add-${i}`} onClick={() => addToLeads(r)}><Plus className="h-3.5 w-3.5 mr-1" /> Add to Leads</Button>;
    }
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Federal Contract Data Prospecting</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none flex items-center gap-2">
          <Building2 className="h-6 w-6 text-gold" aria-hidden="true" /> DoD Contractor Finder
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-2xl">
          Find companies already performing DoD work (awarded contractors) who may need CMMC readiness support —
          then enrich, find the decision-maker, and add them to the pipeline.
        </p>
      </header>

      <Tabs defaultValue="contractors" className="w-full">
        <div className="px-6 md:px-8 pt-5">
          <TabsList>
            <TabsTrigger value="contractors" data-testid="tab-contractors"><Building2 className="h-4 w-4 mr-2" /> DoD Contractor Finder</TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="tab-opportunities"><Radar className="h-4 w-4 mr-2" /> Opportunity Trigger Finder</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="contractors">
          <div className="p-6 md:p-8 pt-4 space-y-6">
            {/* Filters */}
            <div className="rounded-md border border-border bg-card p-5">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Federal organization" value={filters.agency} onChange={(v) => set("agency", v)} testid="cf-agency" />
                <Field label="Sub-agency / subtier" value={filters.subtier} onChange={(v) => set("subtier", v)} placeholder="Dept of the Army" />
                <Field label="Awardee company name" value={filters.awardee_name} onChange={(v) => set("awardee_name", v)} />
                <Field label="NAICS" value={filters.naics} onChange={(v) => set("naics", v)} placeholder="541512" testid="cf-naics" />
                <Field label="Product/Service Code (PSC)" value={filters.psc} onChange={(v) => set("psc", v)} placeholder="D307" />
                <Field label="Place of performance (state)" value={filters.place_of_performance} onChange={(v) => set("place_of_performance", v)} placeholder="VA" />
                <Field label="Contract date from" value={filters.date_from} onChange={(v) => set("date_from", v)} placeholder="YYYY-MM-DD" />
                <Field label="Contract date to" value={filters.date_to} onChange={(v) => set("date_to", v)} placeholder="YYYY-MM-DD" />
                <Field label="Dollars obligated min" type="number" value={filters.dollars_min} onChange={(v) => set("dollars_min", parseInt(v) || 0)} />
                <Field label="Dollars obligated max" type="number" value={filters.dollars_max} onChange={(v) => set("dollars_max", parseInt(v) || 0)} />
                <div className="space-y-1.5">
                  <Label className="text-xs">Keywords</Label>
                  <Input value={filters.keywords} onChange={(e) => set("keywords", e.target.value)} placeholder="cyber, cloud, IT…" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="cf-status">
                    {["Active", "Inactive", "Closed", "Any"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Min DoD exposure score</Label>
                  <Input type="number" className="w-20 h-9" value={filters.min_exposure} onChange={(e) => set("min_exposure", parseInt(e.target.value) || 0)} />
                </div>
                <Button onClick={search} data-testid="cf-search-button" className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                  <Search className="h-4 w-4 mr-2" /> Find Contractors
                </Button>
              </div>
            </div>

            {state.status === "loading" && (
              <div className="flex flex-col items-center py-16 gap-3 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin text-gold-dark" /> Searching USAspending.gov…</div>
            )}
            {state.status === "error" && <p className="text-red-600 text-sm">{state.message}</p>}

            {state.status === "ok" && (
              <div className="rounded-md border border-border bg-card overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border">
                  <div className="text-xs text-muted-foreground">
                    Showing {visibleResults.length} of {state.count} awarded contractors · ranked by DoD exposure
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-navy">Lead Status</Label>
                      <select
                        data-testid="cf-lead-status-filter"
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={leadStatusFilter}
                        onChange={(e) => setLeadStatusFilter(e.target.value)}
                      >
                        {LEAD_STATUS_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                      <Label htmlFor="hide-existing" className="text-xs text-navy cursor-pointer">Hide companies already in leads</Label>
                      <Switch id="hide-existing" data-testid="cf-hide-existing-toggle" checked={hideExisting} onCheckedChange={setHideExisting} />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto table-scroll">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-border text-left">
                        {["Company", "Lead Status", "UEI", "Agency", "Subtier", "NAICS", "PSC", "Contract Value", "Date", "Status", "State", "Exposure", "Likely CUI", "Est. Tier", ""].map((h) => (
                          <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleResults.length === 0 ? (
                        <tr><td colSpan={15} className="px-4 py-10 text-center text-muted-foreground">No contractors match this Lead Status filter.</td></tr>
                      ) : visibleResults.map((r, i) => (
                        <tr key={i} data-testid={`cf-result-${i}`} onClick={() => openDetail(r)} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer">
                          <td className="px-3 py-3 font-medium text-navy max-w-[200px]"><span className="line-clamp-2">{r.company_name}</span></td>
                          <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <LeadStatusBadge r={r} testid={`cf-lead-status-${i}`} />
                          </td>
                          <td className="px-3 py-3 font-mono text-[11px]">{r.uei || "—"}</td>
                          <td className="px-3 py-3 text-xs max-w-[140px] truncate" title={r.agency}>{r.agency || "—"}</td>
                          <td className="px-3 py-3 text-xs max-w-[130px] truncate" title={r.subtier}>{r.subtier || "—"}</td>
                          <td className="px-3 py-3 font-mono text-xs">{r.naics || "—"}</td>
                          <td className="px-3 py-3 font-mono text-xs">{r.psc || "—"}</td>
                          <td className="px-3 py-3 whitespace-nowrap font-medium">{usd(r.award_amount)}</td>
                          <td className="px-3 py-3 text-xs whitespace-nowrap text-muted-foreground">{fmtDate(r.start_date)}</td>
                          <td className="px-3 py-3 text-xs">{r.status}</td>
                          <td className="px-3 py-3 text-xs">{r.place_of_performance || "—"}</td>
                          <td className="px-3 py-3 whitespace-nowrap"><span className={`text-[11px] px-2 py-0.5 rounded border ${EXP_STYLES[r.dod_exposure_label]}`}>{r.dod_exposure_label} · {r.dod_exposure_score}</span></td>
                          <td className="px-3 py-3"><span className={`text-[11px] px-2 py-0.5 rounded ${CUI_STYLES[r.likely_cui]}`}>{r.likely_cui}</span></td>
                          <td className="px-3 py-3 text-[11px] max-w-[130px]"><span className="line-clamp-2">{r.estimated_tier}</span></td>
                          <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <RowAction r={r} i={i} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="opportunities">
          <OpportunityTriggerFinder />
        </TabsContent>
      </Tabs>

      {/* Contractor detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cf-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-lg text-navy pr-8">{selected.company_name}</DialogTitle>
                <DialogDescription>Federal contract data, CMMC readiness guidance, and lead actions for this contractor.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <div className="flex flex-wrap gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded border ${EXP_STYLES[selected.dod_exposure_label]}`}>DoD exposure {selected.dod_exposure_label} · {selected.dod_exposure_score}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${CUI_STYLES[selected.likely_cui]}`}>Likely CUI: {selected.likely_cui}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-gold/20 text-gold-dark">{selected.estimated_tier}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  <Info label="UEI" value={selected.uei} mono />
                  <Info label="Agency" value={selected.agency} />
                  <Info label="Subtier" value={selected.subtier} />
                  <Info label="NAICS" value={selected.naics} mono />
                  <Info label="PSC" value={selected.psc} mono />
                  <Info label="Contract value" value={usd(selected.award_amount)} />
                  <Info label="Period" value={`${fmtDate(selected.start_date)} – ${fmtDate(selected.end_date)}`} />
                  <Info label="Status" value={selected.status} />
                </div>
                {selected.description && <div><p className="text-xs text-muted-foreground mb-1">Contract description</p><p className="text-slate-700 leading-relaxed">{selected.description}</p></div>}

                {/* Company Research Checklist */}
                <div className="rounded-md border border-border p-4">
                  <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-3 flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> Company Research Checklist</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {CHECKLIST.map((item) => (
                      <label key={item} data-testid={`cf-check-${item.toLowerCase().replace(/ /g, "-")}`} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={!!checks[item]} onCheckedChange={(v) => setChecks((c) => ({ ...c, [item]: v }))} />
                        <span className={checks[item] ? "line-through text-muted-foreground" : "text-slate-700"}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Sales Assistant output */}
                {!guidance ? <p className="text-xs text-muted-foreground">Generating guidance…</p> : (
                  <div className="space-y-3 rounded-md border-l-4 border-l-gold bg-slate-50 border border-border p-4">
                    <Guide title="Why this company may be a fit" text={guidance.why_fit} />
                    <Guide title="Likely CMMC pain angle" text={guidance.cmmc_pain_angle} />
                    <Guide title="Suggested first email" text={guidance.suggested_email} pre />
                    <Guide title="Suggested phone opener" text={guidance.suggested_phone} />
                    <Guide title="Suggested Apollo title search" text={guidance.apollo_title_search} />
                    <Guide title="Recommended next action" text={guidance.recommended_next_action} />
                  </div>
                )}

                {selected.source_link && <a href={selected.source_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gold-dark text-sm hover:underline"><ExternalLink className="h-3.5 w-3.5" /> View award on USAspending.gov</a>}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
                <Button variant="outline" onClick={() => setShowDM(true)} data-testid="cf-find-dm" className="bg-navy text-white hover:bg-navy-700 border-navy">
                  <Wand2 className="h-4 w-4 mr-2 text-gold" /> Find Decision Maker
                </Button>
                {selected.lead_status && selected.lead_status !== "New Prospect" ? (
                  <>
                    {selected.lead_status === "Possible Duplicate" ? (
                      <Button onClick={() => setReviewMatch({ rec: selected, match: selected.lead_match })} data-testid="cf-detail-review" className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                        <GitCompare className="h-4 w-4 mr-2" /> Review Match
                      </Button>
                    ) : (
                      <Button onClick={() => goToLead(selected.lead_match?.matchedLeadId)} data-testid="cf-detail-view" className="bg-navy text-white hover:bg-navy-700">
                        <Eye className="h-4 w-4 mr-2" /> {selected.lead_status === "Closed Lost / Nurture" ? "Reopen Lead" : selected.lead_status === "Existing Customer" ? "View Customer" : "View Lead"}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button onClick={() => addToLeads(selected)} disabled={adding} data-testid="cf-detail-add" className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                    <Plus className="h-4 w-4 mr-2" /> {adding ? "Adding…" : "Add to Leads"}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selected && (
        <FindDecisionMaker
          open={showDM}
          onOpenChange={setShowDM}
          lead={{ company_name: selected.company_name, website: selected.website || "", phone: selected.phone || "", email: selected.email || "", cage_code: selected.cage_code || "" }}
          hasExistingContact={false}
          onApply={(contact, note) => addToLeads(selected, contact, note)}
          onSaveAdditional={(contact, note) => addToLeads(selected, contact, note)}
        />
      )}

      {/* Review Match (possible duplicate) modal */}
      <Dialog open={!!reviewMatch} onOpenChange={(v) => !v && setReviewMatch(null)}>
        <DialogContent className="max-w-2xl" data-testid="cf-review-match-dialog">
          {reviewMatch && (() => {
            const { rec, match } = reviewMatch;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-lg text-navy flex items-center gap-2">
                    <GitCompare className="h-5 w-5 text-gold-dark" aria-hidden="true" /> Review Possible Duplicate
                  </DialogTitle>
                  <DialogDescription>Compare this contractor with the likely existing lead before adding, to avoid creating a duplicate.</DialogDescription>
                </DialogHeader>
                <div className="py-2 space-y-4 text-sm">
                  <div className="rounded-md bg-gold/10 border border-gold/30 px-3 py-2 flex items-center justify-between">
                    <span className="text-navy font-medium">Match confidence</span>
                    <span className="font-display text-lg font-bold text-gold-dark">{match?.matchConfidence ?? 0}%</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-md border border-border p-3">
                      <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-2">Contractor Finder result</p>
                      <MiniRow label="Company" value={rec.company_name} />
                      <MiniRow label="UEI" value={rec.uei} mono />
                      <MiniRow label="CAGE" value={rec.cage_code} mono />
                      <MiniRow label="Website" value={rec.website} />
                      <MiniRow label="Agency" value={rec.agency} />
                    </div>
                    <div className="rounded-md border border-navy/20 bg-navy/5 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-navy font-semibold mb-2">Existing lead</p>
                      <MiniRow label="Company" value={match?.matchedLeadName} />
                      <MiniRow label="Deal stage" value={match?.matchedDealStage} />
                      <MiniRow label="Bucket" value={match?.matchedBucket} />
                      <MiniRow label="Last updated" value={match?.matchedUpdatedAt ? fmtDate(match.matchedUpdatedAt) : ""} />
                      <MiniRow label="Owner" value={match?.matchedOwner} />
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Matched on: </span>
                    {(match?.matchedFields || []).map((f) => (
                      <span key={f} className="text-[11px] px-2 py-0.5 rounded border bg-slate-100 text-slate-700 mr-1">{f}</span>
                    ))}
                  </div>
                </div>
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="ghost" onClick={() => setReviewMatch(null)} data-testid="cf-review-cancel">Cancel</Button>
                  <Button variant="outline" onClick={() => { const id = match?.matchedLeadId; setReviewMatch(null); goToLead(id); }} data-testid="cf-review-open-existing" className="border-navy text-navy">
                    <ArrowRight className="h-4 w-4 mr-1" /> Open Existing Lead
                  </Button>
                  <Button onClick={() => addToLeads(rec, null, null, true)} disabled={adding} data-testid="cf-review-add-anyway" className="bg-gold text-navy hover:bg-gold-dark font-semibold">
                    <Plus className="h-4 w-4 mr-1" /> {adding ? "Adding…" : "Add Anyway"}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadStatusBadge({ r, testid }) {
  const m = r.lead_match || {};
  const badge = (
    <span data-testid={testid} className={`text-[11px] px-2 py-0.5 rounded border cursor-default ${LEAD_STATUS_STYLES[r.lead_status] || LEAD_STATUS_STYLES["New Prospect"]}`}>
      {r.lead_status || "New Prospect"}
    </span>
  );
  if (!m.matchedLeadId) return badge;
  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild><span>{badge}</span></HoverCardTrigger>
      <HoverCardContent className="w-72 text-xs" data-testid={`${testid}-details`}>
        <p className="font-display font-bold text-navy mb-2">{m.matchedLeadName}</p>
        <MiniRow label="Matched field" value={(m.matchedFields || []).join(", ")} />
        <MiniRow label="Confidence" value={`${m.matchConfidence}%`} />
        <MiniRow label="Deal stage" value={m.matchedDealStage} />
        <MiniRow label="Bucket" value={m.matchedBucket} />
        <MiniRow label="Last updated" value={m.matchedUpdatedAt ? fmtDate(m.matchedUpdatedAt) : ""} />
        <MiniRow label="Owner" value={m.matchedOwner} />
      </HoverCardContent>
    </HoverCard>
  );
}

function MiniRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-navy text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Info({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-border/60">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={`text-navy text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}
function Guide({ title, text, pre }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-1">{title}</p>
      {pre ? <pre className="text-sm whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">{text}</pre> : <p className="text-sm text-slate-700 leading-relaxed">{text}</p>}
    </div>
  );
}
